import * as React from 'react'
import * as Switch from 'primitives/switch'
import { SentryFilterTagOneClickUpgradeToggle } from '@repo/sentry-utils/constants'
import { PaymentStatus } from '@repo/graphql-types'
import type { UpgradeSource } from '../../upgrade-sources'
import { PAYMENT_ENTRY_POINTS } from '../../payments-dialog/analytics'
import { SpinnerIcon, XCloseIcon } from 'primitives/icons'
import { tw } from 'utils/classnames'
import { B } from '../app-routes'
import { ActivateTrialDialogs } from './activate-trial-dialogs'
import { ActivateTrialRoot, useActivateTrialRootContext } from './activate-trial-root'

export interface ActivateTrialToggleProps {
  ariaLabel?: string
  className?: string
  noReload?: boolean
  onSettled?: () => void
  subscriptionType?: 'all-access' | 'studio-ai'
  upgradeSource: UpgradeSource
}

function ActivateTrialToggleControl({
  ariaLabel = 'Toggle yearly subscription',
  className,
}: Pick<ActivateTrialToggleProps, 'ariaLabel' | 'className'>) {
  const { handleLoggedInTrigger, isActivated, isPending, isUserLoggedIn, result } =
    useActivateTrialRootContext()

  const derivedChecked = isActivated || result?.paymentsStatus === PaymentStatus.Succeeded

  return (
    <>
      <Switch.Root
        checked={derivedChecked}
        onCheckedChange={() => {
          if (derivedChecked) {
            return
          }

          // Match one-click toggle: redirect before blocking on user/activate loading.
          if (!isUserLoggedIn) {
            window.location.href = B.YEARLY_PROMO
            return
          }

          if (isPending) {
            return
          }

          void handleLoggedInTrigger()
        }}
        className={tw(
          'relative inline-flex h-[20px] w-[48px] flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none',
          derivedChecked ? 'bg-[#3AB561]' : 'bg-[#3C3C3C]',
          className,
        )}
        aria-label={ariaLabel}
      >
        <Switch.Thumb
          className={tw(
            'pointer-events-none relative left-[-2px] top-[-4px] inline-block h-[24px] w-[24px] transform rounded-full border-2 ring-0 transition-transform duration-200 ease-in-out',
            derivedChecked ? 'translate-x-[24px]' : 'translate-x-0',
            'bg-service-white border-[#3C3C3C] shadow-md',
          )}
        />
        {isPending ? (
          <img
            src={SpinnerIcon.src}
            width="16"
            height="16"
            className="h-[16px] w-[16px] animate-spin [filter:invert(1)]"
            alt="Processing icon"
          />
        ) : !derivedChecked ? (
          <img
            src={XCloseIcon.src}
            width="10"
            height="10"
            className="h-[16px] w-[16px] [filter:invert(1)]"
            alt="Close icon"
          />
        ) : null}
      </Switch.Root>
    </>
  )
}

export function ActivateTrialToggle({
  ariaLabel = 'Toggle yearly subscription',
  className,
  noReload = false,
  onSettled,
  subscriptionType = 'all-access',
  upgradeSource,
}: ActivateTrialToggleProps) {
  return (
    <ActivateTrialRoot
      entryPoint={PAYMENT_ENTRY_POINTS.ONE_CLICK_UPGRADE_TOGGLE}
      noReload={noReload}
      onSettled={onSettled}
      sentryComponentTag={SentryFilterTagOneClickUpgradeToggle}
      subscriptionType={subscriptionType}
      upgradeSource={upgradeSource}
    >
      <ActivateTrialToggleControl ariaLabel={ariaLabel} className={className} />
      <ActivateTrialDialogs />
    </ActivateTrialRoot>
  )
}
